import {local, select, Selection} from 'd3-selection';
import {Arc, arc, pie, PieArcDatum} from 'd3-shape';
import {color, RGBColor} from 'd3-color';
import {Debugger} from 'debug';
import {Gruppefarge} from "felles-ng2/modell/Gruppefarge";
import {lesZonedDateTimeSerialisert} from "felles-ng2/støttefunksjoner/temporal";
import {erTall} from "felles-ng2/støttefunksjoner/tall";
import {erStreng, erTomEllerNil, trimTilTomStreng} from "felles-ng2/støttefunksjoner/strenger";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {ZonedDateTimeSerialisert} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {Temporal} from "temporal-polyfill";
import ZonedDateTime = Temporal.ZonedDateTime;

const debug: Debugger = require('debug')('ÅrshjulGraf');

export class ÅrshjulGraf {
    static tegnstørrelseMap: { [tegn: string]: number } = {};

    private svg!: Selection<SVGSVGElement, unknown, null, undefined>; //settes av initialiser
    private diameter = 1000;
    private datoringhøyde = 20;
    private tittelringhøyde = 20;
    private ringmargin = 5;
    private minIndreDiameter = 120;
    private indreRadius!: number; //settes av tegnKomplettGraf
    private ringcelleOrginaleData = local();
    private bakgrunnstekstTimer: number | undefined;

    constructor(private målelement: HTMLElement, private konfigurasjon: Konfigurasjon, private oversettelser: Oversettelser) {
        this.initialiser();
    }

    /** Tegner ut grafen med angitte gruppedata */
    tegnGruppedata(grupper: Gruppedata[]): Gruppedata[] {
        debug('tegnGruppedata', {grupper})
        //vi har et maks antall grupper vi kan tegne før den indre diameteren blir for liten
        const grupperSomTegnes = [...grupper];
        while ((this.diameter - (this.beregnetTotalHøydeRinger(grupperSomTegnes) * 2)) * this.skaleringsfaktor() < this.minIndreDiameter && grupperSomTegnes.length > 0) {
            debug('tegnGruppedata fjerner gruppe for overholde minIndreDiameter %s < %s', (this.diameter - (this.beregnetTotalHøydeRinger(grupperSomTegnes) * 2)) * this.skaleringsfaktor(), this.minIndreDiameter, grupper)
            grupperSomTegnes.pop();
        }
        this.tegnKomplettGraf(grupperSomTegnes);
        return grupperSomTegnes;
    }

    /** ytre diameter til det ytterste segmentet, i absolutte pixler (bredden på hele svg-elementet) */
    ytreDiameterAbsolutt() {
        return this.diameter * this.skaleringsfaktor();
    }

    /** indre diameter til det minste segmentet med celler, i absolutte pixler */
    indreDiameterAbsolutt() {
        return (this.indreRadius * 2) * this.skaleringsfaktor();
    }

    /** beregner skaleringsfaktor basert på tilgjengelig høyde. */
    skaleringsfaktor() {
        const faktiskDiameter = this.svg.node()?.clientHeight ?? 0; //Dersom svg ikke tegnet ut enda blir dette 0
        return faktiskDiameter / this.diameter;
    }

    static ukenummer(dato: ZonedDateTimeSerialisert | ZonedDateTime): number {
        const zonedDateTime = typeof dato === 'string' ? lesZonedDateTimeSerialisert(dato) : dato;
        const uke = zonedDateTime.toPlainDate().weekOfYear!;
        return uke > 52 ? 52 : uke; //@todo vi tar ikke hensyn til år med 53 uker
    }

    //private metoder

    private initialiser() {
        this.initTegnstørrelseMap();
        this.svg = this.svgRotelement();
        let radius = this.diameter / 2;
        this.tegnDatoring('mndring', this.måneddata(), radius, radius -= this.datoringhøyde, 'mnd')
        this.tegnDatoring('ukering', ÅrshjulGraf.ukedata(), radius, radius -= this.datoringhøyde, 'uke')
    }

    private tegnKomplettGraf(grupper: Gruppedata[]) {
        this.indreRadius = (this.diameter / 2) - 40; //mnd og uke opptar 40
        this.svg
          .selectAll<SVGGElement, Gruppedata>('g.yttregruppe')
          .data(grupper, (d: Gruppedata) => d.gruppeId)
          .join('g')
          .attr('class', 'yttregruppe e2e-gruppekontainer')
          .attr('id', d => `yttregruppe_${d.gruppeId}`)
          .each((gruppedata, grpIndeks, g) => {
              const yttregruppeElem: Selection<SVGGElement, Gruppedata, null, undefined> = select(g[grpIndeks]);
              this.tegnSegmenterMedTittel(yttregruppeElem, `grptittel_${grpIndeks}`, this.indreRadius -= this.ringmargin, this.indreRadius -= this.tittelringhøyde, gruppedata.gruppetittel)

              yttregruppeElem
                .selectAll<SVGGElement, Segmentring>('g.grupperinger')
                .data((d: Gruppedata) => d.ringer, (d: Segmentring) => d.id)
                .join('g')
                .attr('class', d => `grupperinger ${d.cssklasse}`)
                .attr('id', d => `grupperinger_${d.id}`)
                //roter dersom det finnes innslag som spenner over nyttår
                .attr('transform', (d, i) => `rotate(${(d.uker[0].ukenummer == 1) ? 0 : (360 / 52) * (d.uker[0].ukenummer - 1)})`)
                .each((ring, ringIndeks, g) => {
                    const gruppeElem: Selection<SVGGElement, Segmentring, null, undefined> = select(g[ringIndeks]) as any;
                    gruppeElem.data
                    //ingen padding på første ring (dvs ingen padding mellom segment med tittel og påfølgende
                    const ytreRadius = ringIndeks == 0 ? this.indreRadius : this.indreRadius -= this.ringmargin;
                    const indreRadius = this.indreRadius -= ring.høyde;
                    this.tegnSegmenterMedCeller(gruppeElem, ring.id, ring.uker, ytreRadius, indreRadius)
                });
          });

        //vis bakgrunnstekst med forsinkelse i tomme ringer
        window.clearTimeout(this.bakgrunnstekstTimer);
        this.bakgrunnstekstTimer = window.setTimeout(() => {
            this.svg.selectAll('g.bakgrunn textPath').attr('class', '');
            window.setTimeout(() => {
                this.svg.selectAll('g.bakgrunn textPath').attr('class', 'bakgrunn_tekst_skjult');
            }, 10 * 1000);
        }, 2 * 1000);
    }

    private tegnDatoring(id: string, data: CelleInfo[], outerRadius: number, innerRadius: number, stilklasse: string) {
        const datosegment = arc<PieArcDatum<CelleInfo>>()
          .outerRadius(outerRadius)
          .innerRadius(innerRadius);
        const tekststi = this.tekstbue(datosegment);

        const ukerpath = this.svg.append('g')
          .selectAll(`g.${stilklasse}`)
          .data(pie<CelleInfo>().value(d => d.bredde).sort(null)(data))
          .join('g')
          .attr('class', stilklasse)

        ukerpath.append('path')
          .attr('d', datosegment)
          .attr('id', (d, i) => `datoring_${id}_${i}`)
          .attr('class', d => `ramme ${this.markertUkeKlasse(d.data.ukenummer)}`)

        ukerpath.append('path')
          .attr('id', (d, i) => `datoring_tekst_${id}_${i}`)
          .attr('d', tekststi)
          .style('fill', 'none');

        ukerpath.append('text')
          .attr('dy', (d, i) => 3)
          .append('textPath')
          .attr('xlink:href', (d, i) => `#datoring_tekst_${id}_${i}`)
          .attr('startOffset', '50%')
          .style('text-anchor', 'middle')
          .text((d, i) => d.data.tittel);
    }

    private tegnSegmenterMedTittel(forelder: Selection<SVGGElement, Gruppedata, null, undefined>, id: string, yttreRadius: number, indreRadius: number, tittel: string) {
        const tittelTekstBue = this.tekstbue(arc().outerRadius(yttreRadius).innerRadius(indreRadius));

        //gruppelabel i ring i utkant av ringen med cellene, samt bakgrunnsfarge
        forelder
          .selectAll('g.gruppetittel')
          .data(pie<string>().value(d => 1)(Array(4).fill(tittel))) //viser tittel i hver kvadrant
          .join('g')
          .attr('class', 'gruppetittel')
          .each((d, i, g) => {
              const tittelGruppeElem = select(g[i]);
              //path som liggende tekst kan tegnes langs
              tittelGruppeElem
                .selectAll('path')
                .data(d => [d])
                .join('path')
                .attr('id', () => `ytresegment_${id}_${i}`)
                .attr('d', () => tittelTekstBue(d))
                .style('fill', 'none');
              tittelGruppeElem
                .selectAll('text')
                .data(d => [d])
                .join('text')
                .attr('dy', () => 3)
                .selectAll('textPath')
                .data(d => [d])
                .join('textPath')
                .attr('xlink:href', () => `#ytresegment_${id}_${i}`)
                .attr('startOffset', '50%')
                .style('text-anchor', 'middle')
                .attr('class', 'e2e-gruppetittel')
                .text(() => d.data);
          })
    }

    private tegnSegmenterMedCeller(forelder: Selection<SVGGElement, Segmentring, null, undefined>, id: string, data: CelleInfo[], ytreRadius: number, indreRadius: number) {
        const segmentring = forelder.datum();
        const farge = <RGBColor>color(segmentring.farger.farge);
        const bakgrunnFarge = segmentring.farger.bakgrunnFarge;
        const forgrunnFarge = segmentring.farger.tekstBakgrunnFarge;
        const forgrunnAktivFarge = farge.darker(0.4).formatRgb();
        const forgrunnBlankCelleFarge = 'transparent';
        const forgrunnAktivBlankCelleFarge = farge.copy({opacity: 0.3}).formatRgb();
        const forgrunnMarkertBlankCelleFarge = segmentring.farger.bakgrunnFarge;
        const tekstfarge = segmentring.farger.tekstFarge; //sørger for hvit tekst på mørk bakgrunn
        const bakgrunnSegment = arc<PieArcDatum<any>>()
          .outerRadius(ytreRadius)
          .innerRadius(indreRadius)

        const bakgrunnSegmentTekstBue = this.tekstbue(bakgrunnSegment);

        const celleSegment = arc<PieArcDatum<CelleInfo>>()
          .outerRadius(ytreRadius)
          .innerRadius(indreRadius)
          .padAngle(.01)
          .cornerRadius(3)

        const celleTekstBue = this.tekstbue(celleSegment);

        //bakgrunnsfarge
        forelder
          .selectAll<SVGGElement, unknown>('g.bakgrunn')
          .data(pie<any>().value(d => 1)([1, 1])) //må ha minst to segmenter for å fungere med bakgrunn
          .join('g')
          .attr('class', 'bakgrunn')
          .each((d, i, g) => {
              const gruppeElem = select(g[i]);
              const tekstStiId = `bagkgrts_${id}_${d.index}`

              gruppeElem
                .selectAll('path.bakgrunn')
                .data(pie<number>().value(d => 1)([1]))
                .join('path')
                .attr('class', 'bakgrunn')
                .attr('d', (d) => bakgrunnSegment(d))
                .attr('fill', bakgrunnFarge);

              //path som liggende tekst kan tegnes langs
              gruppeElem
                .selectAll('path.bakgrunn_tekststi')
                .data(d => [<PieArcDatum<number>>d])
                .join('path')
                .attr('id', () => tekstStiId)
                .attr('class', 'bakgrunn_tekststi')
                .attr('d', d => bakgrunnSegmentTekstBue(d))
                .attr('transform', (d, i) => 'rotate(90)')
                .style('fill', 'none');

              //tekst lang sti dersom ingen celler i data
              gruppeElem
                .selectAll('text')
                .data(erStreng(segmentring.bakgrunnstekst) ? [segmentring.bakgrunnstekst] : [])
                .join('text')
                .attr('dy', (d, i) => 3)
                .selectAll('textPath')
                .data(d => [d])
                .join('textPath')
                .attr('xlink:href', d => `#${tekstStiId}`)
                .attr('startOffset', '50%')
                .style('text-anchor', 'middle')
                .attr('class', 'bakgrunn_tekst_skjult')
                .text(d => d);
          });


        //cellene (møter eller årshjulinnslag)
        forelder
          .selectAll<SVGGElement, PieArcDatum<CelleInfo>>('g.celle')
          .data(pie<CelleInfo>().value(d => d.bredde).sort(null)(data),
            (d: PieArcDatum<CelleInfo>) => d.data?.id ?? `${d.index}_${d.data.ukenummer}_${d.data.bredde}`) //bruker CelleInfo.id dersom denne er satt
          .join(
            enter => enter.append('g')
              .attr('opacity', 0)
              .each((d, i, g) => this.ringcelleOrginaleData.set(g[i], d)) //stu unna initiell verdi for senere bruk
              .call(segment => segment.transition()
                .attr('opacity', 1)),

            update => update
              //celler som var på vei ut (exit-overgang med remove()) i en tidligere tegning beholdes dersom de er med i angitte data,
              //uten interrupt() vil pågående exit-overgang fjerne cellen
              .interrupt()
              .attr('opacity', 1)
              .call(segment => segment
                //fade inn celle dersom modell faktisk er endret
                .filter((d, i, g) => {
                    let orginaleData = <PieArcDatum<CelleInfo>>this.ringcelleOrginaleData.get(<SVGElement>g[i]);
                    return !(orginaleData.data.tittel == d.data.tittel &&
                      orginaleData.data.bredde == d.data.bredde);
                })
                .each((d, i, g) => this.ringcelleOrginaleData.set(<Element>g[i], d)) //stu unna initiell verdi for senere bruk
                .attr('opacity', 0)
                .transition()
                .attr('opacity', 1)),

            exit => exit
              .attr('opacity', 1)
              .call(segment => segment.transition()
                .attr('opacity', 0)
                .remove())
          )
          .attr('class', d => `celle e2e-uke e2e-uke-${d.data.ukenummer}`)
          .each((d, i, g) => {
              let høyde = 0; //blir oppdatert lenger ned
              let bredde = 0; //blir oppdatert lenger ned
              const tekst = trimTilTomStreng(d.data.tittel).replace(/\s+/g, ' '); //fjerner doble blanke
              const padding = {vertikal: 4, horisontal: 6};
              const linjehøyde = 12 //burde beregnes
              const erBlankCelle = erTomEllerNil(tekst)
              const celleId = `segment_${id}_${d.index}`
              const tekstStiId = `cellets_${id}_${d.index}`
              const bakgrunn = erBlankCelle ? (this.erMarkertUke(d.data.ukenummer) ? forgrunnMarkertBlankCelleFarge : forgrunnBlankCelleFarge) : forgrunnFarge

              const gruppeElem = select<SVGGElement, PieArcDatum<CelleInfo>>(g[i]);

              //segment med fyll
              const segment = gruppeElem
                .selectAll('path.celle_bakgrunn')
                .data(d => [<PieArcDatum<CelleInfo>>d])
                .join('path')
                .attr('id', () => celleId)
                .attr('class', d => `celle_bakgrunn ${this.markertUkeKlasse(d.data.ukenummer)}`)
                .attr('d', d => celleSegment(d))
                .attr('fill', bakgrunn)
                .call(sti => {
                    høyde = (celleSegment.outerRadius()(sti.datum()) - celleSegment.innerRadius()(sti.datum())) - (2 * padding.vertikal);
                });

              //eventlyttere
              gruppeElem
                .on('click', (event, d: PieArcDatum<CelleInfo>) => {
                    if (erIkkeNil(this.konfigurasjon.vedKlikkPåCelle))
                        this.konfigurasjon.vedKlikkPåCelle(id, d.data, this.somPosisjon(g[i], celleSegment, d));
                })
                .on('mouseenter', (event, d: PieArcDatum<CelleInfo>) => {
                    if (erIkkeNil(this.konfigurasjon.vedMouseEnterCelle))
                        this.konfigurasjon.vedMouseEnterCelle(id, d.data, this.somPosisjon(g[i], celleSegment, d));
                    segment.transition().attr('fill', erBlankCelle ? forgrunnAktivBlankCelleFarge : forgrunnAktivFarge)
                })
                .on('mouseleave', (event, d: PieArcDatum<CelleInfo>) => {
                    if (erIkkeNil(this.konfigurasjon.vedMouseLeaveCelle))
                        this.konfigurasjon.vedMouseLeaveCelle(id, d.data, this.somPosisjon(g[i], celleSegment, d));
                    segment.transition().attr('fill', bakgrunn)
                });

              if (erBlankCelle) return;

              //path som liggende tekst kan tegnes langs
              gruppeElem
                .selectAll('path.celle_tekststi')
                .data(d => [<PieArcDatum<CelleInfo>>d])
                .join('path')
                .attr('id', () => tekstStiId)
                .attr('class', 'celle_tekststi')
                .attr('d', d => celleTekstBue(d))
                .style('fill', 'none')
                .call(sti => {
                    bredde = (<SVGPathElement>sti.node()).getTotalLength();
                });

              //skriv ut tekst - liggende eller stående og over flere linjer om det er plass
              if (bredde > høyde) {
                  //tegn tekst liggende - flytende langs tekststi
                  var tekster = this.beregnLinjer(tekst, bredde, høyde, linjehøyde)
                  let startYOffset = this.beregnStartYOffset(tekster.length, linjehøyde) + 3 //kompanser for offset

                  gruppeElem
                    .selectAll('text')
                    .data(tekster)
                    .join('text')
                    .attr('dy', (d, i) => startYOffset + (i * linjehøyde))
                    .style('fill', tekstfarge)
                    .selectAll('textPath')
                    .data(d => [d])
                    .join('textPath')
                    .attr('xlink:href', () => `#${tekstStiId}`)
                    .attr('startOffset', '50%')
                    .attr('class', 'e2e-årshjulinnslag')
                    .style('text-anchor', 'middle')
                    .text(d => d)

              } else {
                  //tegn tekst stående
                  let tekster = this.beregnLinjer(tekst, høyde, bredde, linjehøyde)
                  let startYOffset = this.beregnStartYOffset(tekster.length, linjehøyde)
                  gruppeElem
                    .selectAll('text')
                    .data(d => [<PieArcDatum<CelleInfo>>d])
                    .join('text')
                    .attr('transform', d => {
                        //plasser senter for text-elementet i midten av segmentet og roter slik at teksten står (justert for over/under horisont)
                        let rotate = (this.midAngle(d) * (180 / Math.PI))
                        rotate += (rotate > 180) ? 90 : -90
                        return `translate(${celleSegment.centroid(d)}) rotate(${rotate}) translate(0, ${linjehøyde / 4})`;
                    })
                    .attr('text-anchor', 'middle')
                    .style('fill', tekstfarge)
                    .selectAll('tspan')
                    .data(tekster)
                    .join('tspan')
                    .attr('x', '0')
                    .attr('y', (d, i) => startYOffset + (i * linjehøyde))
                    .text(d => d);

              }
          })
    }

    private svgRotelement() {
        return select(this.målelement)
          .append('svg')
          .attr('width', '100%')
          .attr('height', '100%')
          .attr('viewBox', `${-this.diameter / 2} ${-this.diameter / 2} ${this.diameter} ${this.diameter}`);
    }

    private måneddata(): CelleInfo[] {
        const måneder = [
            this.oversettelser.oversett(i18n('jan')),
             this.oversettelser.oversett(i18n('feb')),
             this.oversettelser.oversett(i18n('mar')),
             this.oversettelser.oversett(i18n('apr')),
             this.oversettelser.oversett(i18n('mai')),
             this.oversettelser.oversett(i18n('jun')),
             this.oversettelser.oversett(i18n('jul')),
             this.oversettelser.oversett(i18n('aug')),
             this.oversettelser.oversett(i18n('sep')),
             this.oversettelser.oversett(i18n('okt')),
             this.oversettelser.oversett(i18n('nov')),
             this.oversettelser.oversett(i18n('des'))
        ];
        return måneder.map(mnd => ({tittel: mnd, bredde: 1, ukenummer: 0})); //@todo flytte ukenummer til ekstra?
    }

    static ukedata(): CelleInfo[] {
        //@todo år kan ha 53 uker..
        return Array.from({length: 52}, (_, index) => index + 1).map((d, i) => ({tittel: `${d}`, bredde: 1, ukenummer: d}));
    }

    private initTegnstørrelseMap() {
        select(this.målelement)
          .append('div')
          .attr('id', 'tegnstoerrelse')
          .selectAll('span')
          .data('abcdefghijklmnopqrstuvwxyzæøåABCDEFGHIJKLMNOPQRSTUVWXYZÆØÅ1234567890!\'"#$%&/()=?-.,_:;@* '.split(''))
          .enter()
          .append('span')
          .text(d => d)
          .each((p, i, g) => ÅrshjulGraf.tegnstørrelseMap[p] = g[i].offsetWidth);
        debug('initTegnstørrelseMap', ÅrshjulGraf.tegnstørrelseMap);
    }

    /** returnerer ny arc som representerer linjen som går horinsontalt midt i angitt segment,
     *  med start og slutt snudd dersom segmentet er under horisonten */
    private somMidestiltTekstsegment(segment: Arc<any, any>): Arc<any, PieArcDatum<any>> {
        return arc<PieArcDatum<any>>()
          .outerRadius(d => segment.outerRadius()(d) + ((segment.innerRadius()(d) - segment.outerRadius()(d)) / 2))
          .endAngle(d => this.erUnderHorisonten(d) ? d.startAngle : d.endAngle)
          .startAngle(d => this.erUnderHorisonten(d) ? d.endAngle : d.startAngle)
    }

    /** returnerer en funksjon som generer en bue (path) som kan benyttes for å tegne tekst langs i et segment.
     * Buen er plassert i senter av segmentet - dvs tekst tegnet lang buen vil være sentrert i buen - liggende */
    private tekstbue(omsluttendeSegment: Arc<any, any>): (d: PieArcDatum<any>) => string {
        const tekstsegment = this.somMidestiltTekstsegment(omsluttendeSegment);
        const førsteLinjeIArc = /(^.+?)L/;
        return (d: PieArcDatum<any>) => {
            //fjerner de to siste punkten (L0 0 og Z) slik at buen kun en en krummet linje og ikke et sluttet kakestykke
            return førsteLinjeIArc.exec(tekstsegment(d)!)![1];
        }
    }

    /** beregner lengde på angitt tekst (i form at en char array) */
    private tekstlengde(tegn: string[]): number {
        return tegn.map(t => ÅrshjulGraf.tegnstørrelseMap[t] || ÅrshjulGraf.tegnstørrelseMap['a']).reduce((total, lengde) => total + lengde, 0);
    }

    /** Brekker angitt tekst i linjer ihht angitte maks høyde/bredde */
    private beregnLinjer(tekst: string, makslengde: number, maksHøyde: number, linjehøyde: number): string[] {
        const linjer = []
        const bokstaver = tekst.split('').reverse()
        while (bokstaver.length > 0) {
            let linje: string[] = [];

            while (this.tekstlengde(linje) < makslengde && bokstaver.length > 0) {
                linje.push(bokstaver.pop()!)
                if (linje[0] === ' ') linje.pop() //ignorer blankt tegn i start av linje
            }
            //ignorer blankt tegn i slutten av linje
            if (linje[linje.length - 1] === ' ') linje.pop()
            //legg tilbake siste tegn (når vi kommer ut av while er linjen for lang), men ignorer at linjen er for lang dersom den kun inneholder ett tegn
            if (this.tekstlengde(linje) > makslengde && linje.length > 1) bokstaver.push(linje.pop()!)
            //ignorer blankt tegn i slutten av linje (dersom forrige linje gjorde en pop kan det på nytt være et blankt tegn på slutten av linja)
            if (linje[linje.length - 1] === ' ') linje.pop()
            //dersom det er mindre enn to tegn siden siste ' ' spoler vi tilbake til siste ' '
            if (bokstaver.length > 0 && linje.lastIndexOf(' ') > -1 && linje.length - linje.lastIndexOf(' ') <= 2) { // linja slutter med kun ett tegn etter et blankt tegn
                bokstaver.push(linje.pop()!)
                bokstaver.push(linje.pop()!)
            }
            linjer.push(linje.join(''))
        }

        const maksAntallLinjer = Math.max(Math.abs(maksHøyde / linjehøyde), 1) //sørger for at vi ser minst en linje
        var tekster = linjer.slice(0, Math.min(maksAntallLinjer, linjer.length));
        return tekster
    }

    /** beregner relativt y-offset start når multiple linjer skal tegnes ut sentrert omkring y=0 */
    private beregnStartYOffset(antallLinjer: number, linjehøyde: number): number {
        return antallLinjer == 1 ? 0 :
          -(Math.floor(antallLinjer / 2) * linjehøyde) + (antallLinjer % 2 == 0 ? linjehøyde / 2 : 0)
    }

    /** beregner vinkelen midt mellom start og slutt vinkel i angitt segment */
    private midAngle(d: PieArcDatum<any>): number {
        return d.startAngle + ((d.endAngle - d.startAngle) / 2);
    }

    /** konverterer fra grader (0-360) til radiens (0-2*pi)*/
    private somRadians(grader: number): number {
        return grader * (Math.PI / 180)
    }

    /** absolutt posisjon senter av segment */
    private somPosisjon(svgElement: SVGGraphicsElement, celleSegment: Arc<any, PieArcDatum<CelleInfo>>, d: PieArcDatum<CelleInfo>): Posisjon {
        const pos = celleSegment.centroid(d);
        var ctm = svgElement.getCTM()!; //bruk av ctm gir rikti pos også når svgElementet er transformert
        var x = pos[0] * ctm.a + pos[1] * ctm.c + ctm.e;
        var y = pos[0] * ctm.b + pos[1] * ctm.d + ctm.f;
        return {x: x, y: y, himmelretning: this.himmelretning(d)};
    }

    /** sann dersom angitt segment er under horisonten */
    private erUnderHorisonten(d: PieArcDatum<any>) {
        return d.endAngle > this.somRadians(90) &&
          d.endAngle <= this.somRadians(270);
    }

    private himmelretning(d: PieArcDatum<CelleInfo>): string {
        const sektorStr = 360 / 8;
        let start = 360 - (sektorStr / 2);
        if (d.startAngle >= this.somRadians(start)) return 'N';
        if (d.startAngle >= this.somRadians(start -= sektorStr)) return 'NV';
        if (d.startAngle >= this.somRadians(start -= sektorStr)) return 'V';
        if (d.startAngle >= this.somRadians(start -= sektorStr)) return 'SV';
        if (d.startAngle >= this.somRadians(start -= sektorStr)) return 'S';
        if (d.startAngle >= this.somRadians(start -= sektorStr)) return 'SØ';
        if (d.startAngle >= this.somRadians(start -= sektorStr)) return 'Ø';
        if (d.startAngle >= this.somRadians(start -= sektorStr)) return 'NØ';
        return 'N';
    }

    private beregnetTotalHøydeRinger(grupper: Gruppedata[]): number {
        return this.datoringhøyde * 2 //datoringene
          + grupper.length * this.tittelringhøyde //gruppetitlene (tar ikke med margin da første ring i gruppe ikke har margin)
          + grupper.flatMap(g => g.ringer).map(r => r.høyde + this.ringmargin).reduce((total, høyde) => total + høyde, 0); //ringene
    }

    private erMarkertUke(ukenummer: number) {
        return erTall(this.konfigurasjon.markertUke) && ukenummer == this.konfigurasjon.markertUke;
    }

    private markertUkeKlasse(ukenummer: number) {
        return this.erMarkertUke(ukenummer) ? ' markert' : '';
    }
}


export interface Konfigurasjon {
    markertUke?: number;
    vedKlikkPåCelle?: (ringId: string, celle: CelleInfo, pos: Posisjon) => void;
    vedMouseEnterCelle?: (ringId: string, celle: CelleInfo, pos: Posisjon) => void;
    vedMouseLeaveCelle?: (ringId: string, celle: CelleInfo, pos: Posisjon) => void;
}

export interface Posisjon {
    x: number;
    y: number;
    himmelretning: string
}

export interface Gruppedata {
    gruppeId: string;
    gruppetittel: string;
    ringer: Segmentring[];
}

export interface Segmentring {
    id: string;
    cssklasse: string;
    farger: Gruppefarge;
    høyde: number;
    bakgrunnstekst: string | null;
    uker: CelleInfo[];
}

export interface CelleInfo {
    id?: string;
    tittel: string;
    bredde: number;
    ukenummer: number;
    ekstra?: any;
}
